import * as React from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import {
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { startSxpProxy } from '../../../utils/api'
import CreateLabOrder from './CreateLabOrder'
import { LABS_PROJECT_ID } from '../../../utils/constants'
import { formatInvoiceKeyData } from '../../lms/utils'
import { InvoiceData, toastOptions } from '../../lms/models'
import {
  dateDifference,
  formatReadableDate,
} from '../../bedManagement/IpdVisit/IpdSummary/Constants'
// import { Patient } from '../../bedManagement/IpdVisit/IpdSummary/Interfaces'
import { GetLabDepartments } from '../../lms/components/search/GetLabDepartments'
import { useInvoicePDFGenerator } from '../../lms/components/dashboard/InvoicePdfLib'
import CloseIcon from '@mui/icons-material/Close'
import {
  GetCampLabOrdersApi,
  UpdateLaborderAPi,
  UpdateLabOrderBarcodeApi,
  resendNotification,
} from '../../../services/CampManagementService'
import { toast } from 'react-toastify'
import ToastMessage from '../../lms/components/ToastMessage'
import EditIcon from '@mui/icons-material/Edit'
// import MessageIcon from '@mui/icons-material/Message'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { getLocationAddress } from '../../patients/utils'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '../../../app/hooks'
import {
  selectLocationById,
  selectLocations,
  selectSelectedLocationId,
} from '../../location/locationSlice'
import { prefix } from '../../administration/constants'
import KeycloakService from '../../../utils/keycloakService'
import { LAB_ORDER_STATUS } from '../Constants'

const styles = () => ({
  labListContainer: {
    marginTop: '16px',
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
    // height: '80vh',
  },
  tableHead: {
    '& td, & th': {
      backgroundColor: '#4682b4',
      color: '#ffffff',
      fontWeight: 'bold',
    },
  },
  tableHeadCell: {
    backgroundColor: '#4682b4',
    color: '#ffffff',
    fontWeight: 'bold',
  },
  tableRow: {
    '& > *': { borderBottom: 'unset' },
  },
  tableCellCollapse: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  innerTableContainer: {
    margin: '8px',
  },
  innerTableHead: {
    backgroundColor: '#f5f5f5',
  },
  innerTableHeadCell: {
    fontWeight: 600,
  },
  innerTable: {
    border: '1px solid #ddd',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  iconBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
  },
  createOrderBtn: {
    textTransform: 'capitalize',
  },
})

function Row({
  row,
  patient,
  index,
  labDepartments,
  barcodeValue,
  onBarcodeChange,
  onBarcodeFocus,
  inputRef,
}: any) {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [open, setOpen] = React.useState(false)
  const { generatePDF } = useInvoicePDFGenerator()
  const locations = useAppSelector(selectLocations)
  const selectedLoc = useAppSelector(selectSelectedLocationId)
  const selcetedLocID = useAppSelector((state) =>
    selectLocationById(selectedLoc, state)
  )
  const prefixIdentifier = selcetedLocID?.resource?.identifier?.find(
    (item: { system?: string; value?: string }) => item?.system === prefix
  )
  const locationAddress = getLocationAddress(
    prefixIdentifier?.value || '',
    locations
  )

  const handleBarcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    onBarcodeChange(index, newValue)
  }

  const handleBarcodeFocus = () => {
    onBarcodeFocus(index)
  }

  const handleBarcodeKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      const scannedValue = barcodeValue?.trim()
      onBarcodeChange(index, scannedValue)
      // if (scannedValue) {
      //   console.log(
      //     'Barcode confirmed for LR:',
      //     row?.lr_number,
      //     'Barcode:',
      //     scannedValue
      //   )
      //   // You can add API call here to save barcode
      // }
    }
  }

  const handleInvoicePrint = (item: any, patient: any) => {
    const intent = 'getLabTestsByIds'
    const panelIds = item?.tests?.flatMap(
      (test: any) => test?.parameterIds ?? []
    )
    const testIds = item?.tests?.map((il: any) => il.testId) ?? []
    const state = {
      orderId: item?.id,
      ids: [...panelIds, ...testIds],
    }

    startSxpProxy(LABS_PROJECT_ID, intent, state)
      .then(async (data) => {
        const panel = formatInvoiceKeyData(
          data?.data?.lab_test ?? [],
          item?.status ?? '',
          labDepartments
        )

        const obj: InvoiceData = {
          id: patient?.patientUhid ?? '',
          name:
            patient?.firstName +
              ' ' +
              patient?.middleName +
              ' ' +
              patient?.lastName || '',
          patientAddress: locationAddress,
          age: patient?.age || '',
          gender: patient?.gender.toUpperCase() || '',
          mobile: patient?.phoneNumber ?? '',
          lrNumber: item.lrNumber || '',
          orderedOn: item.orderedOn ?? '',
          completedOn: item.collectionCompletedOn ?? '',
          requestedBy: item.requestedBy ?? item.orderedByName ?? '',
          validatedOn: item.validationCompletedOn ?? '',
          panels: panel,
          summary: item?.summary ?? '',
          orderType: 'OPD',
          fileId: item?.document_id,
          invoiceWithHeaderValue: true,
          logoValue: 'medunited',
        }
        const pdfBlob = await generatePDF(obj)
        const fileUrl = URL.createObjectURL(pdfBlob)

        const iframe = document.createElement('iframe')
        iframe.src = fileUrl
        iframe.width = '100%'
        iframe.height = '100%'
        iframe.style.border = 'none'

        const newWindow = window.open('', '_blank') as Window
        newWindow.document.body.style.margin = '0'
        newWindow.document.body.appendChild(iframe)
        newWindow.document.title = 'Test Results'
      })
      .catch((err) => {
        console.log('Error:', err)
      })
  }

  return (
    <React.Fragment>
      <TableRow className={classes.tableRow}>
        <TableCell>
          <IconButton
            aria-label='expand row'
            size='small'
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align='center'>{index + 1}</TableCell>
        <TableCell component='th' scope='row'>
          {row?.lrNumber}
        </TableCell>
        <TableCell component='th' scope='row'>
          {row?.id}
        </TableCell>
        <TableCell align='left'>{row?.requestedBy}</TableCell>
        <TableCell align='left'>{row?.labOrderStatus}</TableCell>
        <TableCell align='center'>
          {dateDifference(row?.orderedOn, row?.collectionCompletedOn)}
        </TableCell>
        <TableCell align='center'>
          <TextField
            type='text'
            value={barcodeValue ?? row?.barcode}
            onChange={handleBarcodeChange}
            onKeyDown={handleBarcodeKeyDown}
            onFocus={handleBarcodeFocus}
            placeholder='Scan barcode'
            size='small'
            disabled={!!row?.barcode}
            sx={{ width: '300px' }}
            inputRef={inputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <QrCodeScannerIcon sx={{ color: '#666', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
        <TableCell align='left'>
          <IconButton
            onClick={() => handleInvoicePrint(row, patient)}
            // disabled={row?.labOrderStatus != 'COMPLETED' ? true : false}
          >
            <ArticleOutlinedIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell className={classes.tableCellCollapse} colSpan={7}>
            <Collapse in={open} timeout='auto' unmountOnExit>
              <Box className={classes.innerTableContainer}>
                <Typography
                  variant='h6'
                  gutterBottom
                  component='div'
                  fontSize={'14px'}
                  fontWeight={600}
                >
                  Tests
                </Typography>
                <Table
                  size='small'
                  aria-label='purchases'
                  className={classes.innerTable}
                >
                  <TableHead className={classes.innerTableHead}>
                    <TableRow>
                      <TableCell className={classes.innerTableHeadCell}>
                        Test
                      </TableCell>
                      <TableCell
                        className={classes.innerTableHeadCell}
                        align='left'
                      >
                        Doctor
                      </TableCell>
                      <TableCell
                        className={classes.innerTableHeadCell}
                        align='left'
                      >
                        Statues
                      </TableCell>
                      <TableCell
                        className={classes.innerTableHeadCell}
                        align='left'
                      >
                        Date & Time
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row?.tests?.map((panel: any) => (
                      <TableRow key={panel?.testName}>
                        <TableCell component='th' scope='row'>
                          {panel?.testName}
                        </TableCell>
                        <TableCell align='left'>{row?.requestedBy}</TableCell>
                        <TableCell component='th' scope='row'>
                          {panel?.testStatus}
                        </TableCell>
                        <TableCell align='left'>
                          {formatReadableDate(row?.orderedOn)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  )
}

interface LabListProps {
  patientData: any
  onDirtyChange?: (dirty: boolean) => void
}

export default function LabList(props: LabListProps) {
  const { patientData, onDirtyChange } = props
  const theme = useTheme()
  const location = useLocation()
  const campDetails = (location?.state as any)?.camp

  const classes = useCustomStyles(styles, theme)

  const [labOrders, setLabOrders] = React.useState<any[]>([])
  // const [patient, setPatient] = React.useState<Patient>({})
  const [openDialog, setOpenDialog] = React.useState(false)
  const [openEditDialog, setOpenEditDialog] = React.useState(false)
  const [barcodes, setBarcodes] = React.useState<string[]>([])
  const [editBarcodes, setEditBarcodes] = React.useState<{
    [key: number]: string
  }>({})
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null)
  const [hasScannedOnce, setHasScannedOnce] = React.useState(false)
  const [fieldWasEmptyOnFocus, setFieldWasEmptyOnFocus] = React.useState(false)
  const [counterNumber, setCounterNumber] = React.useState<string>('')
  const [loading, setLoading] = React.useState(false)
  const [editLoading, setEditLoading] = React.useState(false)
  const [resendLoading, setResendLoading] = React.useState(false)
  const scanTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const scanCompleteRef = React.useRef<boolean>(false)
  const inputRefs = React.useRef<{ [key: number]: HTMLInputElement | null }>({})

  const labDepartments = GetLabDepartments()

  // Dirty: barcodes changed from saved values but not yet submitted
  const isDirty = React.useMemo(() => {
    return barcodes.some((barcode, index) => {
      const saved = labOrders?.[index]?.barcode || ''
      return barcode?.trim() !== '' && barcode !== saved
    })
  }, [barcodes, labOrders])

  // Notify parent whenever dirty state changes
  React.useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // State for the unsaved-barcodes leave dialog
  const [showLeaveDialog, setShowLeaveDialog] = React.useState(false)
  const pendingNavRef = React.useRef<(() => void) | null>(null)
  // Stable ref so the single-mounted effect always sees the latest isDirty value
  const isDirtyRef = React.useRef(isDirty)
  React.useEffect(() => {
    isDirtyRef.current = isDirty
  }, [isDirty])

  // Patch history ONCE on mount — intercepts every React Router push/replace
  // (sidebar links, tab clicks, list selections, useNavigate calls, <Link>, etc.)
  React.useEffect(() => {
    const originalPushState = window?.history?.pushState?.bind(window?.history)
    const originalReplaceState = window?.history?.replaceState?.bind(
      window?.history
    )

    const interceptPush = function (
      state: any,
      title: string,
      url?: string | null
    ) {
      if (isDirtyRef.current) {
        pendingNavRef.current = () => originalPushState(state, title, url)
        setShowLeaveDialog(true)
        return
      }
      originalPushState(state, title, url)
    }

    const interceptReplace = function (
      state: any,
      title: string,
      url?: string | null
    ) {
      if (isDirtyRef.current) {
        pendingNavRef.current = () => originalReplaceState(state, title, url)
        setShowLeaveDialog(true)
        return
      }
      originalReplaceState(state, title, url)
    }

    const handlePopState = () => {
      if (isDirtyRef.current) {
        // Restore URL so the user stays on the current page
        originalPushState(null, '', window.location.href)
        pendingNavRef.current = () => window.history.go(-1)
        setShowLeaveDialog(true)
      }
    }

    window.history.pushState = interceptPush
    window.history.replaceState = interceptReplace
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
      window.removeEventListener('popstate', handlePopState)
    }
  }, []) // mount/unmount only — isDirty is accessed via ref

  const handleConfirmLeave = () => {
    setShowLeaveDialog(false)
    if (pendingNavRef.current) {
      pendingNavRef.current()
      pendingNavRef.current = null
    }
  }

  const handleCancelLeave = () => {
    setShowLeaveDialog(false)
    pendingNavRef.current = null
  }

  // Warn on browser tab close / refresh
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  // Initialize barcodes array when lab orders change
  React.useEffect(() => {
    if (labOrders.length > 0) {
      // Pre-fill barcodes from existing lab order data, or empty string if not available
      const initialBarcodes = labOrders?.map(
        (order: any) => order?.barcode || ''
      )
      setBarcodes(initialBarcodes)
      setHasScannedOnce(false)

      // Auto-focus the first row that doesn't already have a barcode
      const firstEmptyIndex = labOrders.findIndex(
        (order: any) => !order?.barcode
      )
      if (firstEmptyIndex !== -1) {
        setTimeout(() => {
          inputRefs?.current?.[firstEmptyIndex]?.focus()
        }, 100)
      }

      // // Initialize counter number from first lab order if it exists
      // if (labOrders?.[0]?.counterNumber && !counterNumber) {
      //   setCounterNumber(labOrders?.[0]?.counterNumber)
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labOrders])

  const handleBarcodeFocus = (index: number) => {
    setFocusedIndex(index)
    const wasEmpty = !barcodes?.[index]
    setFieldWasEmptyOnFocus(wasEmpty)
    scanCompleteRef.current = false

    // Clear the field when focused to allow replacement
    if (barcodes?.[index]) {
      const newBarcodes = [...barcodes]
      newBarcodes[index] = ''
      setBarcodes(newBarcodes)
    }
  }

  const handleBarcodeChange = (index: number, value: string) => {
    // If a scan was already complete and user is scanning again, clear first
    if (
      scanCompleteRef?.current &&
      focusedIndex === index &&
      value?.length === 1
    ) {
      value = value?.charAt(0) // Start fresh with just the first character
      scanCompleteRef.current = false
    }

    // Update only the focused row first
    const newBarcodes = [...barcodes]
    newBarcodes[index] = value
    setBarcodes(newBarcodes)
    setCounterNumber(KeycloakService.getUsername() ?? '')

    // Clear any existing timeout
    if (scanTimeoutRef?.current) {
      clearTimeout(scanTimeoutRef?.current)
    }

    // Check if all fields are currently empty (except the one being edited)
    const allOthersEmpty = barcodes?.every(
      (barcode, i) => i === index || !barcode
    )

    // If this is the first scan and field was empty, wait for scan to complete
    if (
      !hasScannedOnce &&
      allOthersEmpty &&
      fieldWasEmptyOnFocus &&
      value.length > 0
    ) {
      // Wait 100ms after last character to ensure barcode is complete
      scanTimeoutRef.current = setTimeout(() => {
        const filledBarcodes = new Array(labOrders?.length)?.fill(value)
        setBarcodes(filledBarcodes)
        setHasScannedOnce(true)
        scanCompleteRef.current = true
      }, 100)
    } else if (value?.length > 0) {
      // Mark scan as complete after 100ms of no input
      scanTimeoutRef.current = setTimeout(() => {
        scanCompleteRef.current = true
        // Select all text so next scan replaces it
        if (inputRefs?.current?.[index]) {
          inputRefs?.current?.[index]?.select()
        }
      }, 100)
    }
  }

  const fetchAllLabOrders = async () => {
    setLoading(true)
    try {
      const resp = await GetCampLabOrdersApi(
        patientData?.campId,
        patientData?.patientUhid
      )

      if (resp?.success) {
        const orders = resp?.data
        setLabOrders(orders)
        if (orders?.[0]?.counterNumber) {
          setCounterNumber(orders?.[0]?.counterNumber)
        } else {
          setCounterNumber('')
        }

        // // Fetch patient details
        // if (patientData?.patientUhid) {
        //   const patientResp = await startSxpProxy(
        //     OPD_PROJECT_ID,
        //     'getPatientApi',
        //     {
        //       id: patientData?.patientFhirId,
        //     }
        //   )
        //   if (patientResp?.data) {
        //     const patientResponse = patientResp.data
        //     console.log(patientResponse, 'patientResponse')
        //     setPatient({
        //       patientName: patientResponse?.name?.[0]?.text || '',
        //       dob: patientResponse?.birthDate || '',
        //       gender: patientResponse?.gender || '',
        //       mobileNumber: patientResponse?.telecom?.[0]?.value || '',
        //       muhid:
        //         patientResponse?.identifier?.find((id: any) => id?.system)
        //           ?.value || '',
        //     })
        //   }
        // }
      } else {
        setLabOrders([])
      }
    } catch (err) {
      console.error('Error fetching lab orders:', err)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    if (patientData?.patientUhid && patientData?.campId) {
      fetchAllLabOrders()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData?.patientUhid, patientData?.campId])

  const updateLabOrderForCamp = async () => {
    // Validation: Check if counter number is provided
    // if (!counterNumber || counterNumber?.trim() === '') {
    //   toast(<ToastMessage message='Please enter a counter number' />, {
    //     ...toastOptions,
    //     type: 'error',
    //   })
    //   return
    // }

    // Validation: Check if all barcodes are scanned
    const missingBarcodes = barcodes?.some(
      (barcode) => !barcode || barcode?.trim() === ''
    )

    if (missingBarcodes) {
      toast(
        <ToastMessage message='Please scan barcodes for all lab orders' />,
        {
          ...toastOptions,
          type: 'error',
        }
      )
      return
    }

    const updatedLabOrders = labOrders?.map((order, index) => ({
      labOrderId: order?.id,
      patientId: patientData?.patientUhid,
      barcode: barcodes?.[index] || '',
      counterNumber: counterNumber ?? KeycloakService.getUsername(),
      campId: patientData?.campId,
      testIds: order?.tests?.map((test: any) => test?.testId) || [],
    }))
    setLoading(true)
    try {
      setLoading(true)
      const res: any = await UpdateLaborderAPi(
        updatedLabOrders,
        patientData?.phoneNumber,
        patientData?.firstName
      )
      if (res?.success) {
        toast(
          <ToastMessage
            message={res?.message || 'Lab orders updated successfully'}
          />,
          {
            ...toastOptions,
            type: 'success',
          }
        )
        fetchAllLabOrders()
      }
      fetchAllLabOrders()
    } catch (err: any) {
      console.error('Error updating lab orders:', err)
      toast(
        <ToastMessage
          message={
            err?.message || 'Failed to update lab orders. Please try again.'
          }
        />,
        {
          ...toastOptions,
          type: 'error',
        }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleEditBarcodeChange = (orderId: number, value: string) => {
    setEditBarcodes((prev) => ({
      ...prev,
      [orderId]: value,
    }))
  }

  const handleOpenEditDialog = () => {
    // Initialize edit barcodes with existing barcodes
    const initialEditBarcodes: { [key: number]: string } = {}
    labOrders?.forEach((order) => {
      initialEditBarcodes[order?.id] = order?.barcode || ''
    })
    setEditBarcodes(initialEditBarcodes)
    setOpenEditDialog(true)
  }

  const handleEditBarcodeSubmit = async () => {
    // Validate that all barcodes are filled
    const hasEmptyBarcode = labOrders?.some(
      (order) => !editBarcodes[order?.id]?.trim()
    )

    if (hasEmptyBarcode) {
      toast(
        <ToastMessage message='Please scan barcodes for all lab orders' />,
        {
          ...toastOptions,
          type: 'error',
        }
      )
      return
    }

    // Form the payload as specified
    const payload = {
      campId: patientData?.campId,
      patientUhid: patientData?.patientUhid,
      barcodes: labOrders?.map((order) => ({
        labOrderId: order?.id,
        barcode: editBarcodes[order?.id] || '',
      })),
    }

    setEditLoading(true)
    try {
      const res: any = await UpdateLabOrderBarcodeApi(payload)
      if (res?.success) {
        toast(
          <ToastMessage
            message={res?.message || 'Barcodes updated successfully'}
          />,
          {
            ...toastOptions,
            type: 'success',
          }
        )
        setOpenEditDialog(false)
        fetchAllLabOrders()
      }
    } catch (err: any) {
      console.error('Error updating barcodes:', err)
      toast(
        <ToastMessage
          message={
            err?.message || 'Failed to update barcodes. Please try again.'
          }
        />,
        {
          ...toastOptions,
          type: 'error',
        }
      )
    } finally {
      setEditLoading(false)
    }
  }

  const handleResendLabNotification = async (
    messageType: 'SMS' | 'WHATSAPP'
  ) => {
    if (!patientData?.patientUhid) return
    setResendLoading(true)
    try {
      const response = await resendNotification({
        patientUhid: patientData?.patientUhid || '',
        patientPhoneNumber:
          patientData?.phoneNumber || patientData?.mobile || '',
        patientName: `${patientData?.firstName || ''} ${
          patientData?.lastName || ''
        }`.trim(),
        location: campDetails?.location || '',
        hospital: 'Medunited',
        barcode: barcodes?.find(Boolean) || labOrders?.[0]?.barcode || '',
        notificationType: 'LAB',
        messageType: messageType,
      })
      if (response?.success) {
        toast.success(
          response?.message ??
            'Lab sample collection notification resent successfully'
        )
      } else {
        toast.error(
          response?.message ??
            'Failed to resend lab sample collection notification',
          { autoClose: false, closeOnClick: false }
        )
      }
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <Box className={classes.labListContainer}>
      {/* Unsaved barcodes navigation guard */}
      <Dialog
        open={showLeaveDialog}
        onClose={handleCancelLeave}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Unsaved Barcodes</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            You have scanned barcodes that haven&apos;t been submitted yet. If
            you leave now, your scanned barcodes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleCancelLeave}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmLeave}
          >
            OK, Leave
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        display={'flex'}
        justifyContent={'flex-end'}
        alignItems={'center'}
        mb={2}
        gap={1}
      >
        {/* <Button
          variant='outlined'
          onClick={() => handleResendLabNotification('SMS')}
          className={classes?.createOrderBtn}
          size='small'
          disabled={
            resendLoading ||
            !patientData?.patientUhid ||
            labOrders.length === 0 ||
            labOrders?.some((order) => !order?.lrNumber && !order?.barcode)
          }
          startIcon={<MessageIcon fontSize='small' />}
        >
          {resendLoading ? 'Sending...' : 'Resend Notification'}
        </Button> */}
        <Button
          variant='outlined'
          onClick={() => handleResendLabNotification('WHATSAPP')}
          className={classes?.createOrderBtn}
          size='small'
          disabled={
            resendLoading ||
            !patientData?.patientUhid ||
            labOrders.length === 0 ||
            labOrders?.some((order) => !order?.lrNumber && !order?.barcode)
          }
          startIcon={<WhatsAppIcon fontSize='small' />}
        >
          {resendLoading ? 'Sending...' : 'Resend Notification'}
        </Button>
        <Button
          variant='outlined'
          onClick={handleOpenEditDialog}
          className={classes?.createOrderBtn}
          size='small'
          startIcon={<EditIcon />}
          disabled={
            labOrders.length === 0 ||
            labOrders.some((order) => !order?.lrNumber && !order?.barcode)
          }
          // disabled={labOrders.length === 0 && counterNumber ? false : true}
        >
          Edit Barcodes
        </Button>
        <Button
          variant='contained'
          onClick={() => setOpenDialog(true)}
          className={classes?.createOrderBtn}
          size='small'
        >
          Create Order
        </Button>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth='xl'
        fullWidth
      >
        <DialogTitle>Create Lab Order</DialogTitle>
        <IconButton
          aria-label='close'
          onClick={() => setOpenDialog(false)}
          className={classes.iconBtn}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <CreateLabOrder
            muhId={patientData?.patientUhid || ''}
            fhirId={patientData?.patientFhirId || ''}
            campId={patientData?.campId || ''}
            setOpenDialog={setOpenDialog}
            fetchAllLabOrders={fetchAllLabOrders}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Edit Lab Order Barcodes</DialogTitle>
        <IconButton
          aria-label='close'
          onClick={() => setOpenEditDialog(false)}
          className={classes.iconBtn}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Sl No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>LR Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labOrders?.map((order, index) => (
                    <TableRow key={order.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{order.lrNumber}</TableCell>
                      <TableCell>{order.id}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size='small'
                          placeholder='Scan barcode'
                          value={editBarcodes[order.id] || ''}
                          onChange={(e) =>
                            handleEditBarcodeChange(order.id, e.target.value)
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <QrCodeScannerIcon
                                  sx={{ color: '#666', fontSize: '20px' }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 3,
                gap: 1,
              }}
            >
              <Button
                variant='outlined'
                onClick={() => setOpenEditDialog(false)}
                disabled={editLoading}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                onClick={handleEditBarcodeSubmit}
                disabled={editLoading}
                startIcon={editLoading ? <CircularProgress size={20} /> : null}
              >
                {editLoading ? 'Updating...' : 'Update'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table aria-label='collapsible table' size='small'>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell />
              <TableCell className={classes.tableHeadCell} align='center'>
                Sl No
              </TableCell>
              <TableCell className={classes.tableHeadCell}>Lab Id</TableCell>
              <TableCell className={classes.tableHeadCell}>Order Id</TableCell>
              <TableCell className={classes.tableHeadCell} align='left'>
                Requested By
              </TableCell>
              <TableCell className={classes.tableHeadCell} align='left'>
                Status
              </TableCell>
              <TableCell className={classes.tableHeadCell} align='center'>
                TAT
              </TableCell>
              <TableCell className={classes.tableHeadCell} align='center'>
                Barcode
              </TableCell>

              <TableCell className={classes.tableHeadCell} align='left'>
                View
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align='center'>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {labOrders?.map((row: any, index: number) => (
                  <Row
                    key={row?.lr_number}
                    row={row}
                    patient={patientData}
                    index={index}
                    labDepartments={labDepartments}
                    barcodeValue={barcodes[index] || ''}
                    onBarcodeChange={handleBarcodeChange}
                    onBarcodeFocus={handleBarcodeFocus}
                    inputRef={(el: HTMLInputElement | null) =>
                      (inputRefs.current[index] = el)
                    }
                  />
                ))}
                {labOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      No Lab Orders Found
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* <Box className={classes.paginationContainer}>
        <TablePagination
          component='div'
          count={labOrders.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box> */}
      <Box
        display={'flex'}
        justifyContent={'flex-start'}
        alignItems={'center'}
        mt={2}
      >
        <TextField
          label='Collected By'
          size='small'
          variant='outlined'
          value={counterNumber}
          onChange={(e) => setCounterNumber(e.target.value)}
          style={{ marginRight: 8, minWidth: 140 }}
          disabled
        />
        <Button
          // disabled={!counterNumber}
          disabled={
            loading ||
            (labOrders.length > 0 &&
              labOrders.every(
                (order) => order?.labOrderStatus !== LAB_ORDER_STATUS?.ORDERED
              ))
          }
          variant='contained'
          size='small'
          // onClick={() => fetchAllLabOrders(patientData?.patientUhid)}
          onClick={updateLabOrderForCamp}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}
